import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import type {SgsstExposure} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatCopFull, formatCopMillions, formatCopRange, formatUvtRange} from '@/lib/money'
import {cn} from '@/lib/utils'

// En 0 el marcador se esconde tras el borde izquierdo.
const MARKER_FLOOR = 4

/** Exposición a multa (Decreto 472/2642): siempre un rango, nunca un punto ni $0. */
export function ExposureCard({exposure}: {exposure: SgsstExposure}) {
  const {t} = useTranslation()
  const [normaAbierta, setNormaAbierta] = useState(false)
  const {base, contingent_no_reporte, contingent_mortal} = exposure
  // El medidor sigue el peso perdido (pendiente + incumplido), no solo el no cumple.
  const atFloor = exposure.meter_share === 0
  const hasFindings = exposure.no_cumple_weight > 0
  const markerPct = Math.min(100, Math.max(MARKER_FLOOR, exposure.meter_share * 100))
  // Solo la bandera se acota: centrada en los extremos se sale de la tarjeta; el marcador no se mueve.
  const flagPct = Math.min(88, Math.max(12, markerPct))
  const tier = t(`exposure.tier.${exposure.tier}`)
  const meterNote = atFloor
    ? t('exposure.meterNoteFloor')
    : hasFindings
      ? t('exposure.meterNotePush')
      : t('exposure.meterNotePending')

  return (
    <div className="surface p-6" data-testid="exposure-card">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('exposure.eyebrow')}
          </p>
          <p
            data-testid="exposure-range"
            className={cn(
              'mt-2 font-mono text-[2.5rem] leading-none font-medium tracking-tight',
              atFloor ? 'text-muted-foreground/70' : 'text-foreground'
            )}>
            {formatCopRange(base.cop_low, base.cop_high)}
          </p>
          <p className="mt-3 max-w-[64ch] text-sm text-muted-foreground">
            {atFloor && <span className="font-semibold text-accent-foreground">{t('exposure.sinHallazgosLead')} </span>}
            {t('exposure.subline', {tier, count: exposure.worker_count})}{' '}
            <span className="font-mono text-foreground">{formatUvtRange(base.uvt_low, base.uvt_high)}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
            {t('exposure.badgeLabel')}
          </p>
          <div className="mt-1.5">
            {/* Es una magnitud normativa, no un estado: va por la rampa de U2 (lime, ámbar, naranja). */}
            {atFloor ? (
              <Tag tone="lime">{t('exposure.band.floor')}</Tag>
            ) : (
              <Tag tone={exposure.meter_share >= 0.5 ? 'orange' : 'amber'}>
                {t(exposure.meter_share >= 0.5 ? 'exposure.band.high' : 'exposure.band.mid')}
              </Tag>
            )}
          </div>
        </div>
      </div>

      {exposure.uvt_stale && (
        <p className="mt-3 text-xs text-muted-foreground italic" data-testid="exposure-uvt-stale">
          {t('exposure.uvtStale', {year: exposure.uvt_year})}
        </p>
      )}

      <div className="mt-6 max-w-[640px]">
        <div className="relative h-2.5 rounded-md bg-secondary">
          <div
            className={cn('absolute inset-y-0 left-0 rounded-md', atFloor ? 'exposure-ramp-min' : 'exposure-ramp')}
            style={{width: `${markerPct}%`}}
          />
          <div
            className={cn(
              'absolute -top-7 -translate-x-1/2 rounded-md px-2 py-1 font-mono text-[10px] tracking-wide whitespace-nowrap text-background',
              atFloor ? 'bg-muted-foreground' : 'bg-foreground'
            )}
            style={{left: `${flagPct}%`}}>
            {t('exposure.meterFlag')}
          </div>
          <div
            data-testid="exposure-marker"
            className={cn(
              'absolute -top-1 -bottom-1 w-[3px] -translate-x-1/2 rounded-sm',
              atFloor ? 'bg-muted-foreground' : 'bg-foreground'
            )}
            style={{left: `${markerPct}%`}}
          />
        </div>
        <div className="mt-2.5 flex justify-between font-mono text-[11px] text-muted-foreground">
          <span>{t('exposure.meterFloor', {amount: formatCopMillions(base.cop_low)})}</span>
          <span>{t('exposure.meterCeiling', {amount: formatCopMillions(base.cop_high)})}</span>
        </div>
        <p className="mt-2 text-[12.5px] text-muted-foreground" data-testid="exposure-meter-note">
          {meterNote}
        </p>
      </div>

      <div
        className="mt-4 rounded-lg border border-border bg-secondary/40 px-4 py-3.5"
        data-testid="exposure-contingent">
        <p className="mb-2 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
          {t('exposure.contingentLabel')}
        </p>
        <div className="py-1">
          <div className="flex flex-col gap-0.5 text-[13px] text-muted-foreground sm:flex-row sm:justify-between sm:gap-4">
            <span>{t('exposure.contingentNoReporte')}</span>
            <span className="font-mono whitespace-nowrap">
              {t('exposure.upTo', {amount: formatCopMillions(contingent_no_reporte.cop_high)})}
            </span>
          </div>
        </div>
        <div className="py-1">
          <div className="flex flex-col gap-0.5 text-[13px] text-muted-foreground sm:flex-row sm:justify-between sm:gap-4">
            <span>{t('exposure.contingentMortal')}</span>
            <span className="font-mono whitespace-nowrap">
              {formatCopRange(contingent_mortal.cop_low, contingent_mortal.cop_high)}
            </span>
          </div>
        </div>
        <div className="mt-1.5 flex flex-col gap-0.5 border-t border-dashed border-border pt-2.5 text-[13px] text-muted-foreground sm:flex-row sm:justify-between sm:gap-4">
          <span>{t('exposure.closure')}</span>
          <span className="font-mono whitespace-nowrap">{t('exposure.closureDays')}</span>
        </div>
      </div>

      <p className="mt-5 flex flex-wrap items-center gap-x-2 border-t border-border pt-4 text-[11.5px] leading-relaxed text-muted-foreground">
        <span>{t('exposure.footnote')}</span>
        <span className="font-mono">
          {t('exposure.footnoteCop', {
            low: formatCopFull(base.cop_low),
            high: formatCopFull(base.cop_high),
          })}
        </span>
        <PorQue label={t('exposure.porqueLabel')} testId="exposure-porque" contenidoTestId="exposure-porque-contenido">
          {t('exposure.porque')}
          <button
            type="button"
            data-testid="exposure-porque-norma"
            aria-expanded={normaAbierta}
            onClick={() => setNormaAbierta((previo) => !previo)}
            className="block underline underline-offset-2 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50">
            {t('exposure.porqueNormaLabel')}
          </button>
          {normaAbierta && <p data-testid="exposure-porque-norma-texto">{t('exposure.porqueNorma')}</p>}
        </PorQue>
      </p>
    </div>
  )
}
